import { useState } from "react"
import { Box, TextField } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { ApiQueryAutocomplete } from "../../../../../../../core/api/components/ApiQueryAutocomplete"
import { toCurrency, toDinero, toDineroLossy, toMonetaryAmount } from "../../../../../../currency/currencyLib"
import { useMoney } from "../../../../../../currency/useMoney"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import {
    ConfigQuery,
    ExtraServicesForUserResponse,
    fetchExtraServicesForUser,
} from "../../../../../../../shared/components/createOrder/createOrderApi"
import { ExtraServiceDetails } from "../../../../../../../shared/components/createOrder/createOrderTypes"
import { AddProductInput, AddProductWishInput, ProductionAddLookup } from "../useProductionOrderEdit"

// Catalog products are picked inline in each order-line row; this panel covers the two add flows that are not
// plain catalog products: extra services (their own endpoint) and a free-text product wish.
export enum ProductionAddMode {
    EXTRA_SERVICE = "EXTRA_SERVICE",
    WISH = "WISH",
}

const useStyles = makeStyles()((theme) => ({
    panel: {
        display: "flex",
        flexDirection: "column",
        gap: ".75rem",
        padding: "1rem",
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: ".75rem",
    },
    wishRow: {
        display: "flex",
        alignItems: "flex-start",
        gap: ".75rem",
        flexWrap: "wrap",
    },
    wishName: {
        flex: 1,
        minWidth: "12rem",
    },
    wishPrice: {
        width: "8rem",
    },
    wishQuantity: {
        width: "6rem",
    },
    actions: {
        display: "flex",
        justifyContent: "flex-end",
        gap: ".75rem",
    },
}))

const selectServices = (data: ExtraServicesForUserResponse): ExtraServiceDetails[] => data.extraServices ?? []

// Extra services can be priced flat, per seat or as a min/max band — for the line preview we take whichever
// concrete amount the service exposes (the backend recomputes the authoritative price on save).
const extraServicePrice = (service: ExtraServiceDetails) =>
    service.price ?? service.pricePerSeat ?? service.minimumPrice

type Props = Readonly<{
    mode: ProductionAddMode
    lookup: ProductionAddLookup
    onAddExtraService: (input: AddProductInput) => void
    onAddProductWish: (input: AddProductWishInput) => void
    onClose: () => void
}>

export function ProductionAddLinePicker({ mode, lookup, onAddExtraService, onAddProductWish, onClose }: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const moneyFactory = useMoney({})

    const [wishName, setWishName] = useState("")
    const [wishPrice, setWishPrice] = useState("")
    const [wishQuantity, setWishQuantity] = useState("1")

    const servicesQuery = useApiQuery(fetchExtraServicesForUser, {
        queryName: ConfigQuery.EXTRA_SERVICES_FOR_USER,
        dependencies: {
            userId: lookup.userId,
            merchantId: lookup.merchantId,
            priceListId: lookup.priceListId,
            deliveryType: lookup.deliveryType,
            deliveryTime: lookup.deliveryTime,
        },
        enabled: mode === ProductionAddMode.EXTRA_SERVICE,
        select: selectServices,
    })

    const cancelButton = (
        <LocalizedStrict id="meeting-service-production-view-add-picker-cancel">
            <StandardButton fullWidth={false} variant="outlined" onClick={onClose}>
                Cancel
            </StandardButton>
        </LocalizedStrict>
    )

    if (mode === ProductionAddMode.EXTRA_SERVICE) {
        return (
            <Box className={classes.panel}>
                <ApiQueryAutocomplete<ExtraServiceDetails>
                    fullWidth
                    value={null}
                    options={servicesQuery.data ?? []}
                    isLoading={servicesQuery.isInitialLoading}
                    isError={servicesQuery.isError}
                    onRetry={() => servicesQuery.refetch()}
                    label={l10n.getString("meeting-service-production-view-add-extra-service-autocomplete-label")}
                    getOptionLabel={(option) => {
                        const price = extraServicePrice(option)
                        return price ? `${option.name} — ${moneyFactory.format(toDinero(price))}` : option.name
                    }}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_event, option) => {
                        if (option) {
                            const price = extraServicePrice(option) ?? {
                                amount: 0,
                                scale: 2,
                                currency: lookup.currencyCode,
                            }
                            onAddExtraService({ productId: option.id, name: option.name, price })
                            onClose()
                        }
                    }}
                />
                <Box className={classes.actions}>{cancelButton}</Box>
            </Box>
        )
    }

    const parsedPrice = Number(wishPrice.replace(",", "."))
    const parsedQuantity = Number(wishQuantity)
    const isWishValid =
        wishName.trim().length > 0 && Number.isFinite(parsedPrice) && parsedPrice > 0 && parsedQuantity >= 1

    const handleWishConfirm = () => {
        if (!isWishValid) return
        const price = toMonetaryAmount(toDineroLossy(parsedPrice, toCurrency(lookup.currencyCode)))
        onAddProductWish({ name: wishName.trim(), price, count: Math.floor(parsedQuantity) })
        onClose()
    }

    return (
        <Box className={classes.panel}>
            <Box className={classes.wishRow}>
                <LocalizedStrict id="meeting-service-production-view-add-wish-name" attrs={{ placeholder: true }}>
                    <TextField
                        className={classes.wishName}
                        placeholder=""
                        value={wishName}
                        onChange={(event) => setWishName(event.target.value)}
                    />
                </LocalizedStrict>
                <LocalizedStrict id="meeting-service-production-view-add-wish-price" attrs={{ placeholder: true }}>
                    <TextField
                        className={classes.wishPrice}
                        placeholder=""
                        type="number"
                        value={wishPrice}
                        onChange={(event) => setWishPrice(event.target.value)}
                    />
                </LocalizedStrict>
                <LocalizedStrict id="meeting-service-production-view-add-wish-quantity" attrs={{ placeholder: true }}>
                    <TextField
                        className={classes.wishQuantity}
                        placeholder=""
                        type="number"
                        value={wishQuantity}
                        onChange={(event) => setWishQuantity(event.target.value)}
                    />
                </LocalizedStrict>
            </Box>
            <Box className={classes.actions}>
                {cancelButton}
                <LocalizedStrict id="meeting-service-production-view-add-wish-confirm">
                    <StandardButton
                        fullWidth={false}
                        variant="contained"
                        disabled={!isWishValid}
                        onClick={handleWishConfirm}
                    >
                        Add
                    </StandardButton>
                </LocalizedStrict>
            </Box>
        </Box>
    )
}
